// CarManager - handles car loading, window transparency, and car physics/animation
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CAR } from '../config/constants.js';

export class CarManager {
    constructor(camera, config = {}, preloader = null) {
        this.camera = camera;
        this.car = null;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('car', {
            dispose: () => this.dispose()
        });

        // Dependencies (set via setDependencies)
        this.biomeManager = null;
        this.worldTranslator = null;

        // Base position relative to camera
        this.basePosition = config.basePosition || { ...CAR.BASE_POSITION };

        // Physics/animation state
        this.jostleTime = 0;
        this.prevYaw = 0;
        this.smoothedYawRate = 0;
        this.currentRoll = 0;
        this.currentPitch = 0;

        // Callback for when car is loaded
        this.onCarLoaded = null;
    }

    /**
     * Set dependencies after construction
     */
    setDependencies(deps) {
        this.biomeManager = deps.biomeManager || this.biomeManager;
        this.worldTranslator = deps.worldTranslator || this.worldTranslator;
    }

    /**
     * Load the car model
     */
    loadCar(modelPath = './assets/models/muscle.glb') {
        const loader = new GLTFLoader();

        return new Promise((resolve, reject) => {
            loader.load(
                modelPath,
                (gltf) => {
                    this.car = gltf.scene;

                    // Position car relative to camera
                    this.car.position.set(
                        this.basePosition.x,
                        this.basePosition.y,
                        this.basePosition.z
                    );

                    // Rotate car to face forward (180 degrees)
                    this.car.rotation.y = Math.PI;

                    // Make car a child of camera
                    this.camera.add(this.car);

                    // Make windows transparent
                    this.makeWindowsTransparent();

                    // Call the onCarLoaded callback if set
                    if (this.onCarLoaded) {
                        this.onCarLoaded(this.car);
                    }

                    this._onPreloadComplete?.();
                    resolve(this.car);
                },
                (xhr) => {
                    // Progress callback - optional
                },
                (error) => {
                    console.error('Error loading car model:', error);
                    reject(error);
                }
            );
        });
    }

    /**
     * Make window materials transparent
     */
    makeWindowsTransparent() {
        if (!this.car) return;

        this.car.traverse((child) => {
            if (child.isMesh && child.material) {
                const materialName = child.material.name ? child.material.name.toLowerCase() : '';
                const objectName = child.name ? child.name.toLowerCase() : '';

                const isWindow = materialName.includes('window') ||
                                 materialName.includes('glass') ||
                                 materialName.includes('windshield') ||
                                 objectName.includes('window') ||
                                 objectName.includes('glass') ||
                                 objectName.includes('windshield');

                if (isWindow) {
                    child.material.transparent = true;
                    child.material.opacity = 0.2;
                    child.material.depthWrite = false;
                }
            }
        });
    }

    /**
     * Update car animation and physics
     * @param {number} deltaTime - Time since last frame
     * @param {number} speed - Current speed of the car
     */
    update(deltaTime, speed) {
        if (!this.car) return;

        // Calculate jostle (road bumps)
        let bumpX = 0;
        let bumpY = 0;
        let jostleRotX = 0;
        let jostleRotZ = 0;

        if (speed > 5) {
            const speedIntensity = Math.min(speed / 40, 1.0);
            this.jostleTime += deltaTime * 6;

            const baseVerticalIntensity = 0.0015 * speedIntensity;
            const baseHorizontalIntensity = 0.0008 * speedIntensity;
            const baseRotationalIntensity = 0.0002 * speedIntensity;

            bumpY = Math.sin(this.jostleTime * 2.3) * baseVerticalIntensity +
                    Math.sin(this.jostleTime * 3.7) * baseVerticalIntensity * 0.5;
            bumpX = Math.sin(this.jostleTime * 1.8) * baseHorizontalIntensity;
            jostleRotX = Math.sin(this.jostleTime * 2.1) * baseRotationalIntensity;
            jostleRotZ = Math.sin(this.jostleTime * 2.5) * baseRotationalIntensity * 0.6;
        }

        // Apply jostle to position
        this.car.position.x = this.basePosition.x + bumpX;
        this.car.position.y = this.basePosition.y + bumpY;
        this.car.position.z = this.basePosition.z;

        // Calculate road pitch
        let targetRoadPitch = 0;
        if (this.biomeManager && this.worldTranslator) {
            const groupOffset = this.worldTranslator.getGroupOffset();
            const worldZ = -groupOffset;
            const roadInfo = this.biomeManager.getRoadPositionAtZ(worldZ);
            if (roadInfo?.tangent) {
                const tangent = roadInfo.tangent;
                const horizontalLength = Math.sqrt(tangent.x * tangent.x + tangent.z * tangent.z);
                targetRoadPitch = Math.atan2(tangent.y, horizontalLength);
            }
        }

        // Smooth pitch
        const pitchSmoothing = 0.08;
        this.currentPitch += (targetRoadPitch - this.currentPitch) * pitchSmoothing;

        // Apply pitch (compensating for camera pitch)
        const cameraPitch = this.camera.rotation.x;
        this.car.rotation.x = this.currentPitch - cameraPitch + jostleRotX;

        // Body roll on turns
        const currentYaw = this.camera.rotation.y;
        let yawDelta = currentYaw - this.prevYaw;
        while (yawDelta > Math.PI) yawDelta -= Math.PI * 2;
        while (yawDelta < -Math.PI) yawDelta += Math.PI * 2;
        this.prevYaw = currentYaw;

        // Smooth yaw rate
        const yawRateSmoothing = 0.08;
        this.smoothedYawRate += (yawDelta - this.smoothedYawRate) * yawRateSmoothing;

        // Calculate roll
        const bodyRollScale = 10;
        const maxBodyRoll = 0.07;
        const targetRoll = Math.max(-maxBodyRoll, Math.min(maxBodyRoll, this.smoothedYawRate * bodyRollScale));

        // Smooth roll
        const rollSmoothing = 0.12;
        this.currentRoll += (targetRoll - this.currentRoll) * rollSmoothing;

        this.car.rotation.z = this.currentRoll + jostleRotZ;
    }

    /**
     * Get the car object
     */
    getCar() {
        return this.car;
    }

    /**
     * Set base position
     */
    setBasePosition(position) {
        this.basePosition = { ...position };
    }

    /**
     * Dispose of car resources
     */
    dispose() {
        if (this.car) {
            // Remove from camera
            this.camera.remove(this.car);

            // Dispose all meshes in the car
            this.car.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(m => {
                                if (m.map) m.map.dispose();
                                m.dispose();
                            });
                        } else {
                            if (child.material.map) child.material.map.dispose();
                            child.material.dispose();
                        }
                    }
                }
            });

            this.car = null;
        }
    }
}
